import { Formik, Form } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, Navigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import FormSteps from '../../../FormSteps/FormSteps'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import useUnsavedChanges from '../../../../helpers/hooks/useUnsavedChanges'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import API from '../../../../api/api'
import { UserRoleEnum } from '@/models/features/user.models'
import { selectUserData } from '../../../../reducers/userDataReducer'

import {
  makeStepValidationSchemas,
  initialValues,
  makeValidationSingleReservation,
  makeValidationMultidayReservation,
  preparePayload,
} from './helpers'
import AmenityDetails from './Steps/AmenityDetails'
import PricingAndAvailability from './Steps/PricingAndAvailability'
import ReservationLimits from './Steps/ReservationLimits'
import FormNavButtons from './components/FormNavButtons'

const CreateAmenityForm = () => {
  const { t } = useTranslation(['reservations', 'common'])
  const steps = [
    t('reservations:amenityForms.stepsNav.details'),
    t('reservations:amenityForms.stepsNav.pricingAvailability'),
    t('reservations:amenityForms.stepsNav.reservationLimits'),
  ]
  const stepValidationSchemas = makeStepValidationSchemas(t)
  const [currentStep, setCurrentStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isMultiDayBooking, setIsMultiDayBooking] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const { displaySnackbar } = useSnackbar()
  const navigate = useNavigate()
  const selectedBuilding = useSelector(selectActiveBuilding)

  const userRole = useSelector(selectUserData)?.user.role
  const shouldRedirect = ![
    UserRoleEnum.enum.Administrator,
    UserRoleEnum.enum.Manager,
    UserRoleEnum.enum.PropertyManager,
  ].includes(userRole)

  // Use the reusable hook for unsaved changes handling
  const { clearUnsavedChanges, handleStayOnPage, handleLeaveWithoutSaving, showLeaveConfirmation } =
    useUnsavedChanges({
      hasUnsavedChanges,
      onLeave: () => {
        setHasUnsavedChanges(false)
      },
    })

  const validateStep = async (validateForm, values, setErrors = () => {}) => {
    const stepSchema = stepValidationSchemas[currentStep]
    if (stepSchema) {
      try {
        await stepSchema.validate(values, { abortEarly: false })
        setErrors({})
        return true
      } catch (error) {
        console.error('Step validation failed:', error)
        // Trigger form validation to show errors
        validateForm()
        displaySnackbar(t('reservations:amenityForms.completeRequiredFields'), true)
        return false
      }
    }

    return true
  }

  const handleNext = async (validateForm, values, setErrors) => {
    // Validate only the current step
    const isValid = await validateStep(validateForm, values, setErrors)
    if (isValid && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = async (validateForm, values) => {
    const isValid = await validateStep(validateForm, values)
    if (!isValid) {
      return
    }

    setIsSubmitting(true)
    try {
      const data = preparePayload(values, selectedBuilding.timezone)
      const { image, regulation, agreement, ...rest } = data
      const { data: attachments = [] } = await API.uploadAmenityAttachments({
        image,
        regulation,
        agreement,
      })

      if (attachments.length > 0) {
        for (const attachment of attachments) {
          rest[attachment.field] = attachment.name
        }
      }

      await API.createNewAmenity({ ...rest, buildingId: selectedBuilding.id })
      clearUnsavedChanges()
      setHasUnsavedChanges(false)
      displaySnackbar(t('reservations:amenityForms.amenityCreatedSuccessfully'))
      navigate('/dashboard/building-dashboard/reservations/amenities')
    } catch (error) {
      console.error('Error submitting form:', error)
      displaySnackbar(t('reservations:amenityForms.errorCreatingAmenity'), true)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Get the appropriate validation schema based on amenity type
  const getValidationSchema = () => {
    return isMultiDayBooking
      ? makeValidationMultidayReservation(t)
      : makeValidationSingleReservation(t)
  }

  if (shouldRedirect) {
    return <Navigate replace to="/not-found" />
  }

  return (
    <div className="reservations-amenities-form">
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={getValidationSchema}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({
          setFieldValue,
          handleChange,
          validateForm,
          resetForm,
          setErrors,
          values,
          errors,
          dirty,
        }) => {
          // Track form changes
          if (dirty && !hasUnsavedChanges) {
            setHasUnsavedChanges(true)
          }
          return (
            <>
              <FormSteps
                steps={steps}
                currentStep={currentStep}
                onStepClick={setCurrentStep}
                clickable={false}
              />
              <Form>
                {currentStep === 0 && (
                  <AmenityDetails
                    values={values}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    resetForm={resetForm}
                    errors={errors}
                    setIsMultiDayBooking={setIsMultiDayBooking}
                  />
                )}
                {currentStep === 1 && (
                  <PricingAndAvailability
                    values={values}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    errors={errors}
                  />
                )}
                {currentStep === 2 && (
                  <ReservationLimits
                    values={values}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    errors={errors}
                  />
                )}
              </Form>
              <FormNavButtons
                currentStep={currentStep}
                totalSteps={steps.length}
                onNext={() => handleNext(validateForm, values, setErrors)}
                onBack={handleBack}
                onSubmit={() => handleSubmit(validateForm, values)}
                isSubmitting={isSubmitting}
              />
            </>
          )
        }}
      </Formik>

      <ConfirmPopup
        isOpen={showLeaveConfirmation}
        cancelCallback={handleStayOnPage}
        confirmCallback={handleLeaveWithoutSaving}
        title={t('reservations:amenityForms.leaveWithoutSaving.title')}
        body={t('reservations:amenityForms.leaveWithoutSaving.body')}
        cancelText={t('common:actions.cancel')}
        confirmText={t('reservations:amenityForms.leaveWithoutSaving.leaveAnyway')}
      />
    </div>
  )
}

export default CreateAmenityForm
